import React, { useCallback, useEffect, useRef } from "react";
import { Info, CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  utils                                                              */
/* ------------------------------------------------------------------ */

function cn(...args) {
  return args.filter(Boolean).join(" ");
}

/* ------------------------------------------------------------------ */
/*  variant tokens (Tailwind classes – light + dark)                   */
/* ------------------------------------------------------------------ */

const VARIANTS = {
  info: {
    icon: Info,
    card: "bg-primary-50 dark:bg-primary-900/30 border-primary-100 dark:border-primary-700/40",
    ink: "text-primary-700 dark:text-primary-300",
    badge: "bg-primary-100 dark:bg-primary-800/50 text-primary-700 dark:text-primary-300",
    title: "text-primary-900 dark:text-primary-100",
    body: "text-primary-700/80 dark:text-primary-300/80",
  },
  success: {
    icon: CheckCircle2,
    card: "bg-success-50 dark:bg-success-700/20 border-success-500/20 dark:border-success-500/30",
    ink: "text-success-700 dark:text-success-50",
    badge: "bg-success-100 dark:bg-success-700/40 text-success-700 dark:text-success-50",
    title: "text-success-700 dark:text-success-50",
    body: "text-success-700/80 dark:text-success-50/80",
  },
  warning: {
    icon: AlertTriangle,
    card: "bg-warning-50 dark:bg-warning-700/20 border-warning-500/20 dark:border-warning-500/30",
    ink: "text-warning-700 dark:text-warning-50",
    badge: "bg-warning-100 dark:bg-warning-700/40 text-warning-700 dark:text-warning-50",
    title: "text-warning-700 dark:text-warning-50",
    body: "text-warning-700/80 dark:text-warning-50/80",
  },
  danger: {
    icon: XCircle,
    card: "bg-danger-50 dark:bg-danger-700/20 border-danger-500/20 dark:border-danger-500/30",
    ink: "text-danger-700 dark:text-danger-50",
    badge: "bg-danger-100 dark:bg-danger-700/40 text-danger-700 dark:text-danger-50",
    title: "text-danger-700 dark:text-danger-50",
    body: "text-danger-700/80 dark:text-danger-50/80",
  },
};

/* ------------------------------------------------------------------ */
/*  shared keyframes — injected once, not per toast                    */
/* ------------------------------------------------------------------ */

let stylesInjected = false;
function ensureGlobalStyles() {
  if (stylesInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.setAttribute("data-toast-styles", "true");
  el.textContent = `
    @keyframes toast-in {
      from { opacity: 0; transform: translateX(16px) scale(0.98); }
      to   { opacity: 1; transform: translateX(0) scale(1); }
    }
  `;
  document.head.appendChild(el);
  stylesInjected = true;
}

/* ------------------------------------------------------------------ */
/*  Toast                                                              */
/* ------------------------------------------------------------------ */

/**
 * Toast — a single notification card.
 *
 * @param {string} id
 * @param {"info"|"success"|"warning"|"danger"} [variant="info"]
 * @param {string} [title]
 * @param {React.ReactNode} children
 * @param {number} [duration=5000] - ms before auto-dismiss, 0 disables
 * @param {(id: string) => void} onClose
 * @param {boolean} [exiting]
 */
export function Toast({
  id,
  variant = "info",
  title,
  children,
  duration = 5000,
  onClose,
  exiting = false,
}) {
  const v = VARIANTS[variant] ?? VARIANTS.info;
  const Icon = v.icon;

  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startRef = useRef(null);

  const clear = () => timerRef.current && clearTimeout(timerRef.current);

  const arm = useCallback(() => {
    if (!duration) return;
    clear();
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => onClose?.(id), remainingRef.current);
  }, [duration, id, onClose]);

  useEffect(() => {
    ensureGlobalStyles();
    arm();
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePause = () => {
    if (!duration) return;
    clear();
    const elapsed = Date.now() - startRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
  };

  const handleResume = () => {
    if (!duration) return;
    arm();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      className={cn(
        "pointer-events-auto relative w-full overflow-hidden rounded-xl border shadow-[0_10px_30px_-12px_rgba(15,23,42,0.28)] dark:shadow-black/40 transition-all duration-300 ease-out",
        v.card
      )}
      style={{
        opacity: exiting ? 0 : 1,
        transform: exiting
          ? "translateX(12px) scale(0.98)"
          : "translateX(0) scale(1)",
        animation: exiting
          ? undefined
          : "toast-in 320ms cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div className="flex items-start gap-3 px-4 pt-3.5 pb-4">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            v.badge
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
        </span>

        <div className="min-w-0 flex-1 pt-0.5">
          {title && (
            <p
              className={cn(
                "text-[13.5px] font-semibold leading-tight tracking-[-0.01em]",
                v.title
              )}
            >
              {title}
            </p>
          )}
          {children && (
            <div
              className={cn(
                "text-[13px] leading-snug",
                title && "mt-1",
                v.body
              )}
            >
              {children}
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => onClose?.(id)}
          className={cn(
            "group -m-1 shrink-0 rounded-full p-1 outline-none",
            v.ink
          )}
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full transition-colors group-hover:bg-black/[0.06] dark:group-hover:bg-white/10 group-focus-visible:ring-2 group-focus-visible:ring-offset-1">
            <X className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
          </span>
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ToastContainer                                                     */
/* ------------------------------------------------------------------ */

/**
 * ToastContainer — fixed top-right stack of toasts.
 * @param {{id: string, variant?: string, title?: string, message?: React.ReactNode, duration?: number}[]} toasts
 * @param {(id: string) => void} onClose
 */
export function ToastContainer({ toasts = [], onClose }) {
  return (
    <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-full max-w-[360px] flex-col gap-2.5">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={onClose}>
          {toast.message}
        </Toast>
      ))}
    </div>
  );
}
